import { useRef, type RefObject } from 'react';
import { useIsomorphicLayoutEffect } from 'react-use';

/**
 * A ref that always holds the latest render's value, readable synchronously with no
 * stale-closure window (unlike state, which a callback captured in an earlier render can
 * still see as stale). The write happens in an effect, not inline during render — a hook that
 * returns a ref it just mutated during render trips the "cannot mutate refs during render" lint
 * rule, since the compiler can't prove the returned ref is never read again before the next
 * render. On the client this is `useLayoutEffect`, which still lands before the browser can
 * process any other task — including an async wallet-sheet confirm callback — so a reader has
 * no practical staleness window despite the write no longer happening synchronously in render.
 */
export default function useLatestRef<T>(value: T): RefObject<T> {
	const ref = useRef(value);
	useIsomorphicLayoutEffect(() => {
		ref.current = value;
	}, [value]);
	return ref;
}
